import { useCallback } from "react";
import { useApolloClient } from "@apollo/client/react";

import { AddWidgetDocument, WidgetsDocument } from "#app/lib/data";

/**
 * Copies one widget board's rows into another, widget by widget. Ids never
 * travel — each add sends type/config/position only, so the target board is
 * an independent copy. Concurrent, the `saveToNewView` grammar: each add
 * carries its own cell, so order never matters.
 *
 * The source read is `network-only` on purpose: the board being copied is
 * usually not the mounted one, so the cache has no rows for it, and on a
 * never-opened built-in Dashboard the read is what triggers the server's
 * lazy default seeding — the copy matches what the tab would have shown.
 */
export function useCopyWidgetBoard(): (
  sourceView: string,
  targetView: string,
) => Promise<void> {
  const client = useApolloClient();

  return useCallback(
    async (sourceView: string, targetView: string) => {
      const source = await client.query({
        query: WidgetsDocument,
        variables: { view: sourceView },
        fetchPolicy: "network-only",
      });
      await Promise.all(
        (source.data?.widgets ?? []).map(async (widget) => {
          const res = await client.mutate({
            mutation: AddWidgetDocument,
            variables: {
              view: targetView,
              input: {
                type: widget.type,
                config: (widget.config ?? {}) as Record<string, unknown>,
                x: widget.x,
                y: widget.y,
                w: widget.w,
                h: widget.h,
                schemaVersion: widget.schemaVersion,
              },
            },
          });
          if (!res.data?.addWidget) throw new Error("Failed to copy widget");
        }),
      );
    },
    [client],
  );
}
